import { inspect } from 'util';

import { TsconfigPathsPlugin } from '@esbuild-plugins/tsconfig-paths';
import { type BuildOptions, build } from 'esbuild';
import { ModuleKind, ModuleResolutionKind, ScriptTarget } from 'typescript';

import { createLogger } from '../../utils/logging.js';

import { parseTscArgs } from './args.js';
import { getCustomConditions, readTsBuildConfig, tsc } from './tsc.js';

export type EsbuildConfig = Pick<
  BuildOptions,
  'external' | 'minify' | 'bundle' | 'splitting' | 'treeShaking'
>;

interface EsbuildParameters extends EsbuildConfig {
  debug: boolean;
  type: string | undefined;
}

export const esbuild = async (
  {
    debug,
    type,
    external,
    bundle,
    minify,
    splitting,
    treeShaking,
  }: EsbuildParameters,
  args = process.argv.slice(2),
) => {
  const log = createLogger({ debug });

  const tscArgs = parseTscArgs(args);

  const customConditions = getCustomConditions();

  if (tscArgs.build) {
    log.err(
      'skuba does not currently support the tsc --build flag with esbuild',
    );
    process.exitCode = 1;
    return;
  }

  const parsedCommandLine = readTsBuildConfig(args, log);

  if (!parsedCommandLine || process.exitCode) {
    return;
  }

  const { fileNames: entryPoints, options: compilerOptions } =
    parsedCommandLine;

  log.debug(log.bold('Files'));
  entryPoints.forEach((filepath) => log.debug(filepath));

  log.debug(log.bold('Compiler options'));
  log.debug(inspect(compilerOptions));

  const start = process.hrtime.bigint();

  const isEsm =
    compilerOptions.module !== ModuleKind.CommonJS && type === 'module';

  const canSplit = bundle && isEsm && Boolean(compilerOptions.outDir);

  if (splitting && !canSplit) {
    throw new Error(
      'Splitting requires bundling to be enabled, ESM output format, and outDir to be configured',
    );
  }

  await build({
    bundle,
    minify: bundle && minify,
    splitting: canSplit && splitting,
    treeShaking: bundle && treeShaking,
    external,
    entryPoints,
    format: !isEsm ? 'cjs' : undefined,
    outdir: compilerOptions.outDir,
    logLevel: debug ? 'debug' : 'info',
    logLimit: 0,
    platform:
      compilerOptions.moduleResolution === ModuleResolutionKind.NodeJs ||
      compilerOptions.moduleResolution === ModuleResolutionKind.Node16 ||
      compilerOptions.moduleResolution === ModuleResolutionKind.NodeNext
        ? 'node'
        : undefined,
    plugins: bundle
      ? []
      : [
          // evanw/esbuild#394
          // eslint-disable-next-line new-cap
          TsconfigPathsPlugin({
            tsconfig: { baseUrl: compilerOptions.baseUrl, compilerOptions },
          }),
        ],
    sourcemap: compilerOptions.sourceMap,
    // TODO: as of 0.18, the esbuild CLI no longer infers the target property to
    // avoid ambiguity where multiple `tsconfig.json`s are involved in a build.
    // This would be unusual for a typical SEEK project but we can still explore
    // an explicit setting once we implement `skuba.config.ts` (#1167).
    target: compilerOptions.target
      ? ScriptTarget[compilerOptions.target].toLocaleLowerCase()
      : undefined,
    tsconfig: tscArgs.pathname,
    conditions: customConditions?.length ? customConditions : undefined,
  });

  const end = process.hrtime.bigint();

  log.plain(`Built in ${log.timing(start, end)}.`);

  if (compilerOptions.declaration) {
    await tsc([
      '--declaration',
      '--emitDeclarationOnly',
      ...(tscArgs.project ? ['--project', tscArgs.project] : []),
    ]);
  }
};
